import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import profileService from '../../services/profileService';
import siteSettingsService from '../../services/siteSettingsService';
import { useAuth } from '../../context/AuthContext';
import './AdminProfilePage.css';

const API_URL = '/api';
const BASE_URL = API_URL.replace('/api', '');

// Helper function to convert relative URLs to full URLs
const getFullImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${BASE_URL}${url}`;
  return url;
};

// Helper function to extract relative path from full URLs
const toRelativePath = (url) => {
  if (!url) return '';
  // If it's already a relative path, return as-is
  if (url.startsWith('/')) return url;
  // Extract path from full URLs like http://localhost:5000/uploads/... or https://domain.com/uploads/...
  try {
    const urlObj = new URL(url);
    return urlObj.pathname; // e.g. /uploads/profiles/filename.jpg
  } catch {
    return url;
  }
};

const AdminProfilePage = () => {
  const { user: authUser, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  
  const [profileData, setProfileData] = useState({
    name: '',
    role: 'Super Admin',
    email: '',
    phone: '',
    location: '',
    avatar: ''
  });
  
  const [siteSettings, setSiteSettings] = useState({
    siteTitle: '',
    homeTitle: '',
    siteDescription: '',
    keywords: '',
    copyright: '',
    desktopLogo: '',
    mobileLogo: '',
    emailLogo: '',
    profileImage: '',
    clientId: '',
    clientSecrete: ''
  });
  
  const [socialLinks, setSocialLinks] = useState([]);
  
  const [editForm, setEditForm] = useState({
    name: '',
    companyName: '',
    role: 'Admin',
    location: '',
    whatsapp: '',
    email: '',
    siteTitle: '',
    homeTitle: '',
    siteDescription: '',
    keywords: '',
    copyright: '',
    profileImage: '',
    desktopLogo: '',
    mobileLogo: '',
    emailLogo: '',
    clientId: '',
    clientSecrete: ''
  });
  
  const [editSocialLinks, setEditSocialLinks] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const response = await profileService.getProfile();
      const profile = response?.user || response;
      if (profile) {
        setProfileData({
          name: profile.name || 'Administrator',
          role: profile.role || 'Super Admin',
          email: profile.email || '',
          phone: profile.phone || profile.whatsapp || '',
          location: profile.location || 'Dubai, UAE',
          avatar: profile.avatar || profile.profileImage || ''
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch profile data
      const response = await profileService.getProfile();
      const profile = response?.user || response;
      if (profile) {
        setProfileData({
          name: profile.companyName || profile.name || 'Administrator',
          companyName: profile.companyName || '',
          role: profile.adminRole || profile.role || 'Super Admin',
          email: profile.email || '',
          phone: profile.phone || profile.whatsapp || '',
          location: profile.location || profile.country || 'Dubai, UAE',
          avatar: getFullImageUrl(profile.avatar || profile.profileImage || '')
        });
      }
      
      // Fetch site settings
      const settings = await siteSettingsService.getSettings();
      if (settings) {
        setSiteSettings({
          siteTitle: settings.siteTitle || '',
          homeTitle: settings.homeTitle || '',
          siteDescription: settings.siteDescription || '',
          keywords: settings.keywords || '',
          copyright: settings.copyright || '',
          desktopLogo: getFullImageUrl(settings.desktopLogo || ''),
          mobileLogo: getFullImageUrl(settings.mobileLogo || ''),
          emailLogo: getFullImageUrl(settings.emailLogo || ''),
          profileImage: getFullImageUrl(settings.profileImage || ''),
          clientId: settings.googleClientId || '',
          clientSecrete: settings.googleClientSecret || ''
        });
      }
      
      // Fetch social links
      const links = await siteSettingsService.getSocialLinks();
      if (links && Array.isArray(links)) {
        setSocialLinks(links.map(link => ({
          id: link.id,
          name: link.name || link.platform,
          link: link.link || link.url,
          icon: link.icon,
          visible: link.isVisible !== false
        })));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAccount = () => {
    console.log('Edit mode - siteSettings:', siteSettings);
    console.log('Edit mode - profileData:', profileData);
    console.log('Edit mode - profileImage will be:', siteSettings.profileImage || profileData.avatar);
    
    setEditForm({
      name: profileData.name,
      companyName: profileData.companyName || profileData.name || '',
      role: profileData.role || 'Admin',
      location: profileData.location,
      whatsapp: profileData.phone,
      email: profileData.email,
      siteTitle: siteSettings.siteTitle,
      homeTitle: siteSettings.homeTitle,
      siteDescription: siteSettings.siteDescription,
      keywords: siteSettings.keywords,
      copyright: siteSettings.copyright,
      profileImage: siteSettings.profileImage || profileData.avatar,
      desktopLogo: siteSettings.desktopLogo,
      mobileLogo: siteSettings.mobileLogo,
      emailLogo: siteSettings.emailLogo,
      clientId: siteSettings.clientId,
      clientSecrete: siteSettings.clientSecrete
    });
    setEditSocialLinks([...socialLinks]);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveMessage('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save profile data
      await profileService.updateProfile({
        name: editForm.companyName || editForm.name,
        companyName: editForm.companyName || editForm.name,
        email: editForm.email,
        phone: editForm.whatsapp,
        country: editForm.location,
        avatar: toRelativePath(editForm.profileImage)
      });
      
      // Prepare site settings data - always save relative paths
      const settingsData = {
        siteTitle: editForm.siteTitle,
        homeTitle: editForm.homeTitle,
        siteDescription: editForm.siteDescription,
        keywords: editForm.keywords,
        copyright: editForm.copyright,
        desktopLogo: toRelativePath(editForm.desktopLogo),
        mobileLogo: toRelativePath(editForm.mobileLogo),
        emailLogo: toRelativePath(editForm.emailLogo),
        profileImage: toRelativePath(editForm.profileImage),
        googleClientId: editForm.clientId,
        googleClientSecret: editForm.clientSecrete
      };
      
      console.log('Saving site settings with profileImage:', editForm.profileImage);
      console.log('Full settings data:', settingsData);
      
      // Save site settings
      const result = await siteSettingsService.updateSettings(settingsData);
      console.log('Settings save result:', result);
      
      // Save social links - update existing, create new, delete removed
      const currentLinkIds = socialLinks.map(l => l.id);
      const editLinkIds = editSocialLinks.filter(l => typeof l.id === 'number').map(l => l.id);
      
      // Delete removed links
      for (const link of socialLinks) {
        if (!editLinkIds.includes(link.id)) {
          try {
            await siteSettingsService.deleteSocialLink(link.id);
          } catch (e) {
            console.error('Error deleting social link:', e);
          }
        }
      }
      
      // Update or create links
      for (const link of editSocialLinks) {
        if (link.name && link.link) {
          if (typeof link.id === 'number' && currentLinkIds.includes(link.id)) {
            // Update existing
            try {
              await siteSettingsService.updateSocialLink(link.id, {
                name: link.name,
                link: link.link,
                icon: link.icon,
                isVisible: link.visible === true
              });
            } catch (e) {
              console.error('Error updating social link:', e);
            }
          } else {
            // Create new
            try {
              await siteSettingsService.createSocialLink({
                name: link.name,
                link: link.link,
                icon: link.icon,
                isVisible: link.visible === true
              });
            } catch (e) {
              console.error('Error creating social link:', e);
            }
          }
        }
      }
      
      setSaveMessage('Settings saved successfully!');
      
      // Dispatch event to notify other components (like sidebar) to refresh
      window.dispatchEvent(new CustomEvent('siteSettingsUpdated'));
      
      // Update AuthContext so sidebar name/role update immediately
      if (authUser) {
        const updatedAuthUser = {
          ...authUser,
          companyName: editForm.companyName || editForm.name,
          displayRole: editForm.role,
          country: editForm.location,
          email: editForm.email,
          phone: editForm.whatsapp
        };
        refreshUser(updatedAuthUser);
      }
      
      // Refresh data
      await fetchAllData();
      
      setTimeout(() => {
        setSaving(false);
        setIsEditing(false);
        setSaveMessage('');
      }, 1000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage('Error saving settings. Please try again.');
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleVisibility = async (id) => {
    try {
      const response = await siteSettingsService.toggleSocialLinkVisibility(id);
      // Use the response from backend to update state
      if (response && response.socialLink) {
        setSocialLinks(prev => prev.map(link => 
          link.id === id ? { ...link, visible: response.socialLink.isVisible } : link
        ));
      } else {
        // Fallback to local toggle
        setSocialLinks(prev => prev.map(link => 
          link.id === id ? { ...link, visible: !link.visible } : link
        ));
      }
    } catch (error) {
      console.error('Error toggling visibility:', error);
      // Show error message to user
      setSaveMessage('Error toggling visibility. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const deleteSocialLink = async (id) => {
    try {
      await siteSettingsService.deleteSocialLink(id);
      setSocialLinks(prev => prev.filter(link => link.id !== id));
    } catch (error) {
      console.error('Error deleting social link:', error);
    }
  };

  const addNewSocialLink = () => {
    const newId = Math.max(...editSocialLinks.map(l => l.id), 0) + 1;
    setEditSocialLinks(prev => [...prev, { id: newId, name: '', link: '', icon: null, visible: true }]);
  };

  // Handle image upload for profile and logos
  const handleImageUpload = async (field, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSaveMessage('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSaveMessage('Image size should be less than 5MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('image', file);

      // Determine upload type based on field
      const uploadType = field === 'profileImage' ? 'profile' : 'logo';

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/upload/image?type=${uploadType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Image upload response:', data);
        // Get relative image URL - API returns relative path like /uploads/profiles/...
        const imageUrl = data.imageUrl || data.url;
        // Always store as relative path
        const relativePath = toRelativePath(imageUrl);
        console.log('Storing relative path:', relativePath);
        handleInputChange(field, relativePath);
        setSaveMessage('Image uploaded successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSaveMessage(errorData.error || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      setSaveMessage('Error uploading image');
    }
  };

  const handleSocialLinkChange = (id, field, value) => {
    setEditSocialLinks(prev => prev.map(link =>
      link.id === id ? { ...link, [field]: value } : link
    ));
  };

  const deleteEditSocialLink = (id) => {
    setEditSocialLinks(prev => prev.filter(link => link.id !== id));
  };

  const handleSocialIconUpload = async (id, file) => {
    if (!file) return;
    
    try {
      // Convert file to base64 URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditSocialLinks(prev => prev.map(link =>
          link.id === id ? { ...link, icon: reader.result } : link
        ));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Icon upload error:', error);
      setSaveMessage('Error uploading icon');
    }
  };

  if (loading) {
    return (
      <AdminLayout activeItem="profile">
        <div className="admin-profile-page">
          <div className="profile-loading">
            <div className="loading-spinner"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Edit Mode View
  if (isEditing) {
    return (
      <AdminLayout activeItem="profile">
        <div className="admin-profile-page edit-mode">
          {/* Page Header */}
          <div className="profile-page-header-figma">
            <h1 className="profile-page-title-figma">Edit Admin Account</h1>
            <div className="header-actions">
              <button className="cancel-btn-header" onClick={handleCancel}>
                Cancel
              </button>
              <button className="save-btn-header" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>

          {/* Save Message */}
          {saveMessage && (
            <div className={`save-message-banner ${saveMessage.includes('Error') ? 'error' : 'success'}`}>
              {saveMessage}
            </div>
          )}

          {/* Edit Form Container */}
          <div className="edit-form-container-figma">
            {/* Profile Image Section */}
            <div className="edit-section-figma">
              <h3 className="edit-section-title-figma">Profile Image</h3>
              <div className="profile-image-edit-row">
                <div className="profile-avatar-edit">
                  {editForm.profileImage ? (
                    <img src={getFullImageUrl(editForm.profileImage)} alt="Profile" />
                  ) : (
                    <div className="avatar-placeholder-edit">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="7" r="4" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
                <label className="upload-btn-figma">
                  <input 
                    type="file" 
                    accept="image/*" 
                    style={{ display: 'none' }} 
                    onChange={(e) => handleImageUpload('profileImage', e)}
                  />
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="17,8 12,3 7,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Upload Image
                </label>
              </div>
            </div>

            {/* Basic Info Section */}
            <div className="edit-section-figma">
              <h3 className="edit-section-title-figma">Basic Information</h3>
              <div className="edit-fields-grid">
                <div className="edit-field-figma">
                  <label className="edit-label-figma">Company Name <span style={{fontSize:'11px',color:'#999'}}>(Sidebar name)</span></label>
                  <input 
                    type="text" 
                    className="edit-input-figma" 
                    value={editForm.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    placeholder="Enter company name"
                  />
                </div>
                <div className="edit-field-figma">
                  <label className="edit-label-figma">Name</label>
                  <input 
                    type="text" 
                    className="edit-input-figma" 
                    value={editForm.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter name"
                  />
                </div>
                <div className="edit-field-figma">
                  <label className="edit-label-figma">Role <span style={{fontSize:'11px',color:'#999'}}>(Sidebar role)</span></label>
                  <select
                    className="edit-input-figma"
                    value={editForm.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                  >
                    <option value="Admin">Admin</option>
                    <option value="Super Admin">Super Admin</option>
                    <option value="Sub Admin">Sub Admin</option>
                    <option value="Manager">Manager</option>
                    <option value="Editor">Editor</option>
                  </select>
                </div>
                <div className="edit-field-figma">
                  <label className="edit-label-figma">Location</label>
                  <input 
                    type="text" 
                    className="edit-input-figma" 
                    value={editForm.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="Enter location"
                  />
                </div>
                <div className="edit-field-figma">
                  <label className="edit-label-figma">Email</label>
                  <input 
                    type="email" 
                    className="edit-input-figma" 
                    value={editForm.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email"
                  />
                </div>
                <div className="edit-field-figma">
                  <label className="edit-label-figma">What's App</label>
                  <input 
                    type="text" 
                    className="edit-input-figma" 
                    value={editForm.whatsapp}
                    onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                    placeholder="Enter WhatsApp number"
                  />
                </div>
              </div>
            </div>

            {/* SEO Settings Section */}
            <div className="edit-section-figma">
              <h3 className="edit-section-title-figma">SEO Settings</h3>
              <div className="edit-fields-grid">
                <div className="edit-field-figma">
                  <label className="edit-label-figma">Site Title</label>
                  <input 
                    type="text" 
                    className="edit-input-figma" 
                    value={editForm.siteTitle}
                    onChange={(e) => handleInputChange('siteTitle', e.target.value)}
                    placeholder="Enter site title"
                  />
                </div>
                <div className="edit-field-figma">
                  <label className="edit-label-figma">Home Title</label>
                  <input 
                    type="text" 
                    className="edit-input-figma" 
                    value={editForm.homeTitle}
                    onChange={(e) => handleInputChange('homeTitle', e.target.value)}
                    placeholder="Enter home title"
                  />
                </div>
                <div className="edit-field-figma">
                  <label className="edit-label-figma">Keywords</label>
                  <input 
                    type="text" 
                    className="edit-input-figma" 
                    value={editForm.keywords}
                    onChange={(e) => handleInputChange('keywords', e.target.value)}
                    placeholder="Enter keywords"
                  />
                </div>
                <div className="edit-field-figma">
                  <label className="edit-label-figma">Copyright</label>
                  <input 
                    type="text" 
                    className="edit-input-figma" 
                    value={editForm.copyright}
                    onChange={(e) => handleInputChange('copyright', e.target.value)}
                    placeholder="Enter copyright text"
                  />
                </div>
                <div className="edit-field-figma full-width">
                  <label className="edit-label-figma">Site Description</label>
                  <textarea 
                    className="edit-textarea-figma" 
                    value={editForm.siteDescription}
                    onChange={(e) => handleInputChange('siteDescription', e.target.value)}
                    placeholder="Enter site description"
                    rows={4}
                  />
                </div>
              </div>
            </div>

            {/* Google Login Section */}
            <div className="edit-section-figma">
              <h3 className="edit-section-title-figma">Google Login</h3>
              <div className="edit-fields-grid">
                <div className="edit-field-figma">
                  <label className="edit-label-figma">Client ID</label>
                  <input 
                    type="text" 
                    className="edit-input-figma" 
                    value={editForm.clientId}
                    onChange={(e) => handleInputChange('clientId', e.target.value)}
                    placeholder="Enter Google Client ID"
                  />
                </div>
                <div className="edit-field-figma">
                  <label className="edit-label-figma">Client Secret</label>
                  <input 
                    type="text" 
                    className="edit-input-figma" 
                    value={editForm.clientSecrete}
                    onChange={(e) => handleInputChange('clientSecrete', e.target.value)}
                    placeholder="Enter Google Client Secret"
                  />
                </div>
              </div>
            </div>

            {/* Visual Settings Section */}
            <div className="edit-section-figma">
              <h3 className="edit-section-title-figma">Visual Settings</h3>
              <div className="logos-grid-figma">
                <div className="logo-item-figma">
                  <label className="edit-label-figma">Desktop Logo</label>
                  <div className="logo-preview-figma">
                    {editForm.desktopLogo ? (
                      <img src={getFullImageUrl(editForm.desktopLogo)} alt="Desktop Logo" />
                    ) : (
                      <div className="logo-placeholder-figma">No Logo</div>
                    )}
                  </div>
                  <label className="upload-logo-btn-figma">
                    <input 
                      type="file" 
                      accept="image/*" 
                      style={{ display: 'none' }} 
                      onChange={(e) => handleImageUpload('desktopLogo', e)}
                    />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 20H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Replace
                  </label>
                </div>
                <div className="logo-item-figma">
                  <label className="edit-label-figma">Mobile Logo</label>
                  <div className="logo-preview-figma small">
                    {editForm.mobileLogo ? (
                      <img src={getFullImageUrl(editForm.mobileLogo)} alt="Mobile Logo" />
                    ) : (
                      <div className="logo-placeholder-figma">No Logo</div>
                    )}
                  </div>
                  <label className="upload-logo-btn-figma">
                    <input 
                      type="file" 
                      accept="image/*" 
                      style={{ display: 'none' }} 
                      onChange={(e) => handleImageUpload('mobileLogo', e)}
                    />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 20H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Replace
                  </label>
                </div>
                <div className="logo-item-figma">
                  <label className="edit-label-figma">Email Logo</label>
                  <div className="logo-preview-figma">
                    {editForm.emailLogo ? (
                      <img src={getFullImageUrl(editForm.emailLogo)} alt="Email Logo" />
                    ) : (
                      <div className="logo-placeholder-figma">No Logo</div>
                    )}
                  </div>
                  <label className="upload-logo-btn-figma">
                    <input 
                      type="file" 
                      accept="image/*" 
                      style={{ display: 'none' }} 
                      onChange={(e) => handleImageUpload('emailLogo', e)}
                    />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 20H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Replace
                  </label>
                </div>
              </div>
            </div>

            {/* Social Media Links Section */}
            <div className="edit-section-figma">
              <div className="section-header-with-btn">
                <h3 className="edit-section-title-figma">Social Media Links</h3>
                <button className="add-social-btn-edit" onClick={addNewSocialLink}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Add New
                </button>
              </div>
              
              <div className="social-links-list-edit">
                {editSocialLinks.length === 0 ? (
                  <div className="no-social-links">
                    <p>No social media links yet. Click "Add New" to add one.</p>
                  </div>
                ) : (
                  editSocialLinks.map((link) => (
                    <div key={link.id} className="social-link-card-edit">
                      <div className="social-link-inputs">
                        <div className="edit-field-figma">
                          <label className="edit-label-figma">Name</label>
                          <input 
                            type="text" 
                            className="edit-input-figma" 
                            value={link.name}
                            onChange={(e) => handleSocialLinkChange(link.id, 'name', e.target.value)}
                            placeholder="e.g. Facebook"
                          />
                        </div>
                        <div className="edit-field-figma flex-grow">
                          <label className="edit-label-figma">Link</label>
                          <input 
                            type="text" 
                            className="edit-input-figma" 
                            value={link.link}
                            onChange={(e) => handleSocialLinkChange(link.id, 'link', e.target.value)}
                            placeholder="e.g. https://facebook.com/yourpage"
                          />
                        </div>
                        <div className="edit-field-figma">
                          <label className="edit-label-figma">Icon</label>
                          <div className="icon-upload-wrapper">
                            <div className="icon-preview-edit">
                              {link.icon ? (
                                <img src={link.icon} alt={link.name} />
                              ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                  <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </div>
                            <label className="icon-upload-btn">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Upload
                              <input 
                                type="file" 
                                accept="image/*" 
                                style={{ display: 'none' }}
                                onChange={(e) => handleSocialIconUpload(link.id, e.target.files[0])}
                              />
                            </label>
                          </div>
                        </div>
                        <button className="delete-social-btn" onClick={() => deleteEditSocialLink(link.id)}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Bottom Action Buttons */}
            <div className="bottom-actions-figma">
              <button className="cancel-btn-figma" onClick={handleCancel}>Cancel</button>
              <button className="save-btn-figma" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // View Mode (Default)
  return (
    <AdminLayout activeItem="profile">
      <div className="admin-profile-page">
        {/* Page Header */}
        <div className="profile-page-header-figma">
          <h1 className="profile-page-title-figma">Admin Account</h1>
          <button className="edit-account-btn-figma" onClick={handleEditAccount}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 20H21" stroke="#6D6D6D" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16.5 3.50023C16.8978 3.1024 17.4374 2.87891 18 2.87891C18.2786 2.87891 18.5544 2.93378 18.8118 3.04038C19.0692 3.14699 19.303 3.30324 19.5 3.50023C19.697 3.69721 19.8532 3.93106 19.9598 4.18843C20.0665 4.4458 20.1213 4.72165 20.1213 5.00023C20.1213 5.2788 20.0665 5.55465 19.9598 5.81202C19.8532 6.06939 19.697 6.30324 19.5 6.50023L7 19.0002L3 20.0002L4 16.0002L16.5 3.50023Z" stroke="#6D6D6D" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Edit Account</span>
          </button>
        </div>

        {/* Main Container - Figma Layout */}
        <div className="profile-container-figma">
          {/* Profile Info Section */}
          <div className="profile-info-section-figma">
            {/* Name and Role */}
            <div className="profile-name-role-figma">
              <h2 className="profile-name-text-figma">{profileData.name}</h2>
              <span className="profile-role-text-figma">{profileData.role}</span>
            </div>

            {/* Profile Fields */}
            <div className="profile-fields-figma">
              <div className="profile-field-row-figma">
                <span className="field-label-text-figma">Location</span>
                <span className="field-value-text-figma">{profileData.location || '-'}</span>
              </div>
              <div className="profile-field-row-figma">
                <span className="field-label-text-figma">Email</span>
                <span className="field-value-text-figma">{profileData.email || '-'}</span>
              </div>
              <div className="profile-field-row-figma">
                <span className="field-label-text-figma">What's App</span>
                <span className="field-value-text-figma">{profileData.phone || '-'}</span>
              </div>
            </div>
          </div>

          {/* Social Media Link Section */}
          <div className="social-section-container-figma">
            <h3 className="social-section-title-figma">Social Media Link</h3>
            
            <div className="social-cards-wrapper-figma">
              {socialLinks.length === 0 ? (
                <div className="social-empty-card-figma">
                  <p>No social media links added yet.</p>
                  <button className="add-social-link-btn-figma" onClick={handleEditAccount}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Add Social Media Link
                  </button>
                </div>
              ) : (
                socialLinks.map((link) => (
                  <div key={link.id} className={`social-card-figma ${!link.visible ? 'hidden-link' : ''}`}>
                    {/* Name Column */}
                    <div className="social-card-col-figma name-column-figma">
                      <span className="social-col-label-figma">Name</span>
                      <span className="social-col-value-figma">{link.name}</span>
                    </div>
                    
                    {/* Link Column */}
                    <div className="social-card-col-figma link-column-figma">
                      <span className="social-col-label-figma">Link</span>
                      <span className="social-col-value-figma">{link.link}</span>
                    </div>
                    
                    {/* Icon Column */}
                    <div className="social-card-col-figma icon-column-figma">
                      <span className="social-col-label-figma">Icon</span>
                      <div className="icon-box-figma">
                        <div className="icon-circle-figma">
                          {link.icon ? (
                            <img src={link.icon} alt={link.name} style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8.08836 11.0333C8.02556 11.0333 6.70816 11.0337 6.10056 11.0331C5.78776 11.0329 5.67616 10.9205 5.67616 10.6057C5.67566 9.79712 5.67566 8.98852 5.67616 8.17992C5.67636 7.86912 5.79436 7.75052 6.10316 7.75032C6.71076 7.74992 8.02096 7.75012 8.08836 7.75012V5.98972C8.08876 5.19492 8.23056 4.43412 8.63436 3.73752C9.04776 3.02452 9.64936 2.53612 10.4204 2.25372C10.9142 2.07272 11.4268 2.00052 11.9508 2.00012C12.6064 1.99972 13.262 2.00032 13.9178 2.00152C14.1996 2.00192 14.3246 2.12652 14.3252 2.41032C14.3264 3.17092 14.3264 3.93152 14.3252 4.69192C14.3248 4.97872 14.205 5.09392 13.9168 5.09712C13.3794 5.10292 12.8416 5.09932 12.3048 5.12092C11.7626 5.12092 11.4774 5.38572 11.4774 5.94692C11.4644 6.54052 11.472 7.13472 11.472 7.74992C11.5228 7.74992 13.0658 7.74972 13.787 7.74992C14.1146 7.74992 14.2266 7.86252 14.2266 8.19192C14.2266 8.99612 14.2264 9.80052 14.2258 10.6047C14.2256 10.9293 14.1204 11.0329 13.7908 11.0331C13.0696 11.0335 11.5326 11.0333 11.4654 11.0333V17.5423C11.4654 17.8893 11.3562 17.9999 11.0138 17.9999H8.50916C8.20656 17.9999 8.08856 17.8823 8.08856 17.5797L8.08836 11.0333Z" fill="#3D6AD6"/>
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="social-actions-figma">
                      <button 
                        className={`action-icon-btn-figma eye-btn-figma ${link.visible ? 'visible' : 'hidden'}`} 
                        onClick={() => toggleVisibility(link.id)}
                        title={link.visible ? 'Hide link' : 'Show link'}
                      >
                        {link.visible ? (
                          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="32" height="32" rx="4" fill="#E8F4FC"/>
                            <path d="M16 10C11 10 7 14 6 16C7 18 11 22 16 22C21 22 25 18 26 16C25 14 21 10 16 10Z" stroke="#0397DD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="16" cy="16" r="3" stroke="#0397DD" strokeWidth="1.5"/>
                          </svg>
                        ) : (
                          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="32" height="32" rx="4" fill="#E3E3E3"/>
                            <path d="M15.3889 9.73699C15.81 9.68823 16.2336 9.66388 16.6575 9.66406C21.3167 9.66406 25.0488 12.564 26.6471 16.6568C26.2602 17.6523 25.7374 18.5895 25.0938 19.4419M11.1833 11.1815C9.14538 12.4252 7.56703 14.3522 6.66797 16.6568C8.2663 20.7495 11.9984 23.6495 16.6575 23.6495C18.5877 23.6597 20.4827 23.134 22.1318 22.1311M14.5398 14.539C14.2614 14.8173 14.0407 15.1477 13.8901 15.5113C13.7394 15.8749 13.6619 16.2647 13.6619 16.6583C13.6619 17.0519 13.7394 17.4416 13.8901 17.8052C14.0407 18.1688 14.2614 18.4992 14.5398 18.7776C14.8181 19.0559 15.1485 19.2766 15.5121 19.4273C15.8757 19.5779 16.2655 19.6554 16.659 19.6554C17.0526 19.6554 17.4424 19.5779 17.806 19.4273C18.1696 19.2766 18.5 19.0559 18.7783 18.7776" stroke="#6D6D6D" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M8.66406 8.66406L24.6474 24.6474" stroke="#6D6D6D" strokeLinecap="round"/>
                          </svg>
                        )}
                      </button>
                      <button className="action-icon-btn-figma trash-btn-figma" onClick={() => deleteSocialLink(link.id)}>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="32" height="32" rx="4" fill="#FF0000" fillOpacity="0.08"/>
                          <path d="M21.5 12H19V11.5C19 11.1022 18.842 10.7206 18.5607 10.4393C18.2794 10.158 17.8978 10 17.5 10H14.5C14.1022 10 13.7206 10.158 13.4393 10.4393C13.158 10.7206 13 11.1022 13 11.5V12H10.5C10.3674 12 10.2402 12.0527 10.1464 12.1464C10.0527 12.2402 10 12.3674 10 12.5C10 12.6326 10.0527 12.7598 10.1464 12.8536C10.2402 12.9473 10.3674 13 10.5 13H11V22C11 22.2652 11.1054 22.5196 11.2929 22.7071C11.4804 22.8946 11.7348 23 12 23H20C20.2652 23 20.5196 22.8946 20.7071 22.7071C20.8946 22.5196 21 22.2652 21 22V13H21.5C21.6326 13 21.7598 12.9473 21.8536 12.8536C21.9473 12.7598 22 12.6326 22 12.5C22 12.3674 21.9473 12.2402 21.8536 12.1464C21.7598 12.0527 21.6326 12 21.5 12ZM14 11.5C14 11.3674 14.0527 11.2402 14.1464 11.1464C14.2402 11.0527 14.3674 11 14.5 11H17.5C17.6326 11 17.7598 11.0527 17.8536 11.1464C17.9473 11.2402 18 11.3674 18 11.5V12H14V11.5ZM20 22H12V13H20V22ZM15 15.5V19.5C15 19.6326 14.9473 19.7598 14.8536 19.8536C14.7598 19.9473 14.6326 20 14.5 20C14.3674 20 14.2402 19.9473 14.1464 19.8536C14.0527 19.7598 14 19.6326 14 19.5V15.5C14 15.3674 14.0527 15.2402 14.1464 15.1464C14.2402 15.0527 14.3674 15 14.5 15C14.6326 15 14.7598 15.0527 14.8536 15.1464C14.9473 15.2402 15 15.3674 15 15.5ZM18 15.5V19.5C18 19.6326 17.9473 19.7598 17.8536 19.8536C17.7598 19.9473 17.6326 20 17.5 20C17.3674 20 17.2402 19.9473 17.1464 19.8536C17.0527 19.7598 17 19.6326 17 19.5V15.5C17 15.3674 17.0527 15.2402 17.1464 15.1464C17.2402 15.0527 17.3674 15 17.5 15C17.6326 15 17.7598 15.0527 17.8536 15.1464C17.9473 15.2402 18 15.3674 18 15.5Z" fill="#DC3545"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProfilePage;
